(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const targets = new Set();
  const selectorGroups = [
    {
      type: 'heading',
      selector: [
        '.section-heading',
        '.door-heading',
        '.materials-intro',
        '.faq-heading',
        '.collection-hero',
        '.page-hero',
        '.contact-hero',
        '.contact-form-heading'
      ].join(',')
    },
    {
      type: 'media',
      selector: [
        '.product-grid > .product-card',
        '.story-media',
        '.door-grid > li',
        '.product-gallery',
        '.contact-channel',
        '.anatomy-mobile-dossier'
      ].join(',')
    },
    {
      type: 'card',
      selector: [
        '.material-list > article',
        '.faq-list > details',
        '.cart-row',
        '.footer-grid > *',
        '.anatomy-mobile-points > article'
      ].join(',')
    },
    {
      type: 'line',
      selector: [
        '.catalog-tools',
        '.catalog-more',
        '.materials-note',
        '.story-copy > *',
        '.footer-closing > *',
        '.footer-bottom > *',
        '.collection-toolbar',
        '.page-rte > *',
        '.product-info-inner > *',
        '.contact-field',
        '.contact-submit',
        '.contact-note',
        '[data-reveal]',
        '.search-form',
        '.search-count',
        '.cart-summary',
        '.empty-state > *',
        '.not-found > *'
      ].join(',')
    }
  ];

  const allSelectors = selectorGroups.map((group) => group.selector).join(',');
  let observer = null;
  let heroTimer = 0;

  const motionEnabled = () =>
    Boolean(document.body?.classList.contains('motion-enabled')) && !reducedMotion.matches;

  const markRevealed = (element) => {
    element.classList.add('is-revealed');
    observer?.unobserve(element);
    targets.delete(element);
  };

  const revealAll = () => {
    targets.forEach(markRevealed);
    root.classList.remove('urbanlux-reveal-ready');
  };

  const getType = (element) => {
    const group = selectorGroups.find((item) => element.matches(item.selector));
    return group?.type || 'line';
  };

  const getDelay = (element, index) => {
    const parent = element.parentElement;
    if (!parent) return 0;
    const siblings = Array.from(parent.children).filter((child) => child.matches?.(allSelectors));
    const siblingIndex = siblings.indexOf(element);
    if (siblingIndex < 0) return Math.min((index % 3) * 40, 80);
    return Math.min((siblingIndex % 4) * 55, 165);
  };

  const createObserver = () => {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) markRevealed(entry.target);
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    });
    return observer;
  };

  const collect = (scope = document) => {
    const elements = [];
    if (scope instanceof Element && scope.matches(allSelectors)) elements.push(scope);
    scope.querySelectorAll(allSelectors).forEach((element) => elements.push(element));

    elements.forEach((element, index) => {
      if (
        element.dataset.urbanluxReveal ||
        (element.closest('[data-anatomy-section]') && !element.closest('.anatomy-mobile')) ||
        element.closest('dialog') ||
        element.closest('[data-hero-cinematic]')
      ) return;

      element.dataset.urbanluxReveal = getType(element);
      element.style.setProperty('--ul-reveal-delay', `${getDelay(element, index)}ms`);

      if (!motionEnabled() || !createObserver()) {
        markRevealed(element);
        return;
      }

      const rect = element.getBoundingClientRect();
      const alreadyVisible = rect.top < window.innerHeight * 0.94 && rect.bottom > 0;
      if (alreadyVisible) {
        markRevealed(element);
      } else {
        targets.add(element);
        observer.observe(element);
      }
    });

    if (motionEnabled() && observer) {
      window.requestAnimationFrame(() => root.classList.add('urbanlux-reveal-ready'));
    }
  };

  const introIsActive = () => {
    const intro = document.querySelector('[data-cinematic-intro]');
    return Boolean(intro && !intro.classList.contains('is-dismissed'));
  };

  const startHero = () => {
    const hero = document.querySelector('[data-hero-cinematic]');
    if (!hero || hero.dataset.motionStarted === 'true') return;
    hero.dataset.motionStarted = 'true';

    if (!motionEnabled()) {
      hero.classList.add('is-motion-running');
      return;
    }

    hero.classList.add('is-motion-pending');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        hero.classList.add('is-motion-running');
        hero.classList.remove('is-motion-pending');
        window.clearTimeout(heroTimer);
        heroTimer = window.setTimeout(() => {
          hero.classList.remove('is-motion-running');
          hero.classList.add('is-motion-complete');
        }, 1800);
      });
    });
  };

  const initialise = (scope = document) => {
    collect(scope);
    if (!introIsActive()) startHero();
  };

  const boot = () => initialise(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('urbanlux:intro-dismissed', startHero);
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelectorAll('[data-urbanlux-reveal]').forEach(markRevealed);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target.closest('[data-urbanlux-reveal]');
    if (target) markRevealed(target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-urbanlux-reveal]').forEach((element) => {
      observer?.unobserve(element);
      targets.delete(element);
    });
  });

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) {
      revealAll();
      document.querySelectorAll('[data-hero-cinematic]').forEach((hero) => {
        hero.classList.remove('is-motion-pending', 'is-motion-running');
        hero.classList.add('is-motion-complete');
      });
    } else {
      initialise(document);
    }
  });
})();
